import {
  CSSProperties,
  ForwardedRef,
  MouseEventHandler,
  ReactNode,
  forwardRef,
} from 'react';
import styled from 'styled-components';
import { ColorProps, MarginProps, space } from 'styled-system';

import { Icon } from 'components/Icon/Icon';
import { IconName } from 'components/Icon/IconName';
import { useTabContext } from 'containers/TabContext';
import { linkStyles } from 'theme/theme';
import { IDEALIST_UTM_PARAMS } from 'utils/constants/general/idealistUtmParams';
import { hasUTMParam, isExternalURL, pathWithParams } from 'utils/url';

import { LinkWrapper } from './Link.styled';

export type LinkProps = {
  children?: ReactNode;
  className?: string;
  target?: string;
  href: string;
  rel?: string;
  noIndex?: boolean;
  eventName?: string;
  eventProperties?: Record<string, unknown>;
  iconName?: IconName;
  iconPosition?: 'left' | 'right';
  style?: CSSProperties;
  'data-qa-id'?: string;
  tabIndex?: number;
  onClick?: MouseEventHandler;
  disabled?: boolean;
  title?: string;
  anchorRef?: ForwardedRef<HTMLAnchorElement>;
};

function UnstyledLink({
  anchorRef,
  children,
  href,
  iconName,
  iconPosition,
  eventName,
  eventProperties,
  rel,
  noIndex,
  style,
  target,
  ...props
}: LinkProps) {
  const linkRel = isExternalURL(href)
    ? `noopener noreferrer${noIndex ? ' noindex' : ''}`
    : rel;
  const linkTarget = isExternalURL(href) ? '_blank' : target;
  const isTabbing = useTabContext();

  return (
    <LinkWrapper>
      {iconName && iconPosition === 'left' && (
        <Icon name={iconName} size={20} />
      )}
      <a
        ref={anchorRef}
        href={
          isExternalURL(href) && !hasUTMParam(href)
            ? pathWithParams(href, IDEALIST_UTM_PARAMS)
            : href
        }
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        style={isTabbing ? style : { ...style, outline: 0 }}
        rel={linkRel}
        target={linkTarget}
      >
        {children}
      </a>
      {iconName && iconPosition === 'right' && (
        <Icon name={iconName} size={20} />
      )}
    </LinkWrapper>
  );
}

const UnstyledLinkWithRef = forwardRef<
  HTMLAnchorElement,
  Omit<LinkProps, 'anchorRef'>
>((props, ref) => (
  <UnstyledLink
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
    anchorRef={ref}
  />
));

export const Link = styled(UnstyledLinkWithRef)<MarginProps & ColorProps>`
  ${linkStyles}
  ${space}
`;
